/**
 * Ojar Free Samples Manager
 * - Limits samples to MAX 2
 * - Shows "FREE" for all sample products
 * - Excludes samples from checkout totals
 * - Works with 'sample-product' tagged items
 */

(function () {
  'use strict';

  const MAX_FREE_SAMPLES = 2;
  let isProcessing = false;
  let observerDebounceTimeout = null; // Debounce for MutationObserver

  /**
   * Initialize on page load
   */
  function init() {
    console.log('[FreeSamples] Initializing...');

    // Run checks
    enforceSampleLimit();
    displayFreePrices();
    interceptSampleForms();

    // Watch for cart changes
    observeCartChanges();

    // Override fetch to catch cart operations
    overrideFetch();

    console.log('[FreeSamples] Initialized successfully');
  }

  /**
   * Display FREE for complimentary samples section only
   * Cart items are handled by Liquid template server-side
   */
  function displayFreePrices() {
    // Only handle complimentary samples section (the sample picker cards)
    // Cart item FREE labels are rendered by Liquid template
    document.querySelectorAll('.oj-complimentary-samples__wrapper .card-wrapper .price').forEach(el => {
      if (!el.closest('.ojar-free-hidden') && !el.querySelector('.ojar-free-price') && !el.closest('s')) {
        el.innerHTML = '<span class="ojar-free-price" style="color: #2e7d32; font-weight: bold;">FREE</span>';
      }
    });
  }

  /**
   * Helper: Replace price elements in an item with FREE (ONLY the final price, not strikethrough)
   */
  function replacePricesWithFree(item) {
    // Only replace the FINAL price display - NOT strikethrough prices
    // Target specific elements that show the final/sale price
    const finalPriceSelectors = [
      '.cart-item__totals .price--end:not(s .price--end)',
      '.cart-item__totals > .price:not(s .price)',
      '.oj-line-item__price',
      '.cart-item__final-price'
    ];

    // Check if Liquid OR JS already rendered FREE (prevent duplication)
    if (item.querySelector('.oj-sample-free-tag') || item.querySelector('.ojar-free-price')) {
      // Already has FREE from server-side Liquid or client-side JS, don't add another
      return;
    }

    let replaced = false;
    finalPriceSelectors.forEach(selector => {
      item.querySelectorAll(selector).forEach(el => {
        // Skip if inside a <s> tag (strikethrough) or already has FREE
        if (!el.closest('s') && !el.querySelector('.ojar-free-price') && !el.classList.contains('oj-sample-free-tag') && !replaced) {
          el.innerHTML = '<span class="ojar-free-price" style="color: #2e7d32; font-weight: bold;">FREE</span>';
          replaced = true;
        }
      });
    });

    // If we couldn't find specific final price, try other price elements
    if (!replaced) {
      item.querySelectorAll('.price--end, .price-item--sale').forEach(el => {
        if (!el.closest('s') && !el.classList.contains('oj-sample-free-tag') && !replaced) {
          el.innerHTML = '<span class="ojar-free-price" style="color: #2e7d32; font-weight: bold;">FREE</span>';
          replaced = true;
        }
      });
    }

    // Hide strikethrough if it says FREE (edge case)
    item.querySelectorAll('s').forEach(s => {
      if (s.textContent.includes('FREE')) {
        s.style.display = 'none';
      }
    });
  }

  /**
   * Enforce the sample limit
   */
  async function enforceSampleLimit() {
    if (isProcessing) return;
    isProcessing = true;

    try {
      const cart = await fetchCart();
      let sampleCount = 0;
      const sampleItems = [];

      // Check each cart item for sample-product tag
      for (const item of cart.items) {
        // Check DOM first for tag data
        const cartItemEl = document.querySelector(`[data-key="${item.key}"]`);
        let isSample = cartItemEl?.dataset?.tags?.includes('sample-product') ||
          cartItemEl?.dataset?.isSample === 'true' ||
          cartItemEl?.classList?.contains('cart-item__sample');

        // If not found in DOM, fetch product to check tags
        if (!isSample) {
          try {
            const product = await fetchProduct(item.handle);
            isSample = product.tags && product.tags.includes('sample-product');
          } catch (e) {
            console.warn('[FreeSamples] Could not verify product:', item.handle);
          }
        }

        if (isSample) {
          sampleCount += item.quantity;
          sampleItems.push({
            key: item.key,
            quantity: item.quantity,
            handle: item.handle
          });
        }
      }

      console.log('[FreeSamples] Sample count:', sampleCount, '/', MAX_FREE_SAMPLES);

      // Update UI counters
      updateCounters(sampleCount);

      // Handle limit logic
      if (sampleCount > MAX_FREE_SAMPLES) {
        // Remove excess samples
        const excess = sampleCount - MAX_FREE_SAMPLES;
        console.log('[FreeSamples] Removing', excess, 'excess samples');
        await removeExcessSamples(sampleItems, excess);
        return; // Will reload
      }

      if (sampleCount >= MAX_FREE_SAMPLES) {
        // Hide sample selection and disable buttons
        hideSampleSelection();
        disableSampleButtons();
      } else {
        // Show sample selection and enable buttons
        showSampleSelection();
        enableSampleButtons();
      }

      // Always display FREE prices
      displayFreePrices();

    } catch (error) {
      console.error('[FreeSamples] Error:', error);
    } finally {
      isProcessing = false;
    }
  }

  /**
   * Update counter displays
   */
  function updateCounters(currentCount) {
    document.querySelectorAll('.oj-sample__current-count').forEach(el => {
      el.textContent = currentCount;
    });
    document.querySelectorAll('.oj-sample__total-count').forEach(el => {
      el.textContent = MAX_FREE_SAMPLES;
    });
  }

  /**
   * Hide the sample selection container
   */
  function hideSampleSelection() {
    document.querySelectorAll('.oj-complimentary-samples__container').forEach(el => {
      el.style.display = 'none';
    });
  }

  /**
   * Show the sample selection container
   */
  function showSampleSelection() {
    document.querySelectorAll('.oj-complimentary-samples__container').forEach(el => {
      el.style.display = '';
    });
  }

  /**
   * Disable sample add/increase buttons
   */
  function disableSampleButtons() {
    // Disable add buttons in complimentary section
    document.querySelectorAll('.oj-complimentary-samples__wrapper button, .oj-complimentary-samples__wrapper .quick-add__submit').forEach(btn => {
      btn.disabled = true;
      btn.style.pointerEvents = 'none';
      btn.style.opacity = '0.5';
    });

    // Disable plus buttons on sample cart items
    document.querySelectorAll('.cart-item__sample .quantity__button[name="plus"]').forEach(btn => {
      btn.disabled = true;
      btn.style.pointerEvents = 'none';
      btn.style.opacity = '0.5';
    });
  }

  /**
   * Enable sample add/increase buttons
   */
  function enableSampleButtons() {
    document.querySelectorAll('.oj-complimentary-samples__wrapper button, .oj-complimentary-samples__wrapper .quick-add__submit').forEach(btn => {
      btn.disabled = false;
      btn.style.pointerEvents = '';
      btn.style.opacity = '';
    });

    document.querySelectorAll('.cart-item__sample .quantity__button[name="plus"]').forEach(btn => {
      btn.disabled = false;
      btn.style.pointerEvents = '';
      btn.style.opacity = '';
    });
  }

  /**
   * Remove excess samples from cart
   */
  async function removeExcessSamples(sampleItems, excess) {
    let remaining = excess;

    for (let i = sampleItems.length - 1; i >= 0 && remaining > 0; i--) {
      const item = sampleItems[i];
      const toRemove = Math.min(remaining, item.quantity);
      const newQty = item.quantity - toRemove;

      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: item.key, quantity: newQty })
      });

      remaining -= toRemove;
    }

    // Refresh cart dynamically (don't close drawer)
    await refreshCartDrawer();

    // Re-run sample limit check
    setTimeout(() => {
      enforceSampleLimit();
      displayFreePrices();
    }, 300);
  }

  /**
   * Intercept form submissions for sample products
   */
  function interceptSampleForms() {
    document.querySelectorAll('.oj-complimentary-samples__wrapper form').forEach(form => {
      if (form.dataset.freeSampleIntercepted) return;
      form.dataset.freeSampleIntercepted = 'true';

      form.addEventListener('submit', async function (e) {
        e.preventDefault();
        e.stopPropagation();

        // Check current sample count
        const cart = await fetchCart();
        let count = await countSamplesInCart(cart);

        if (count >= MAX_FREE_SAMPLES) {
          alert(`You can only select ${MAX_FREE_SAMPLES} free samples maximum.`);
          return false;
        }

        // Submit via fetch
        const formData = new FormData(form);
        await fetch('/cart/add.js', {
          method: 'POST',
          body: formData
        });

        // Refresh cart dynamically (don't close drawer)
        await refreshCartDrawer();

        // Re-run sample limit check
        setTimeout(() => {
          enforceSampleLimit();
          displayFreePrices();
        }, 300);
      }, true);
    });
  }

  /**
   * Count sample products in cart
   */
  async function countSamplesInCart(cart) {
    let count = 0;

    for (const item of cart.items) {
      try {
        const product = await fetchProduct(item.handle);
        if (product.tags && product.tags.includes('sample-product')) {
          count += item.quantity;
        }
      } catch (e) { }
    }

    return count;
  }

  /**
   * Observe cart DOM for changes
   */
  function observeCartChanges() {
    const targets = [
      document.getElementById('CartDrawer'),
      document.getElementById('cart_page'),
      document.querySelector('cart-drawer'),
      document.querySelector('cart-items')
    ].filter(Boolean);

    targets.forEach(target => {
      const observer = new MutationObserver(() => {
        // Debounce to prevent rapid-fire updates and memory issues
        if (observerDebounceTimeout) {
          clearTimeout(observerDebounceTimeout);
        }
        observerDebounceTimeout = setTimeout(() => {
          if (!isProcessing) {
            enforceSampleLimit();
            displayFreePrices();
            interceptSampleForms();
          }
        }, 150);
      });
      observer.observe(target, { childList: true, subtree: true });
    });
  }

  /**
   * Override fetch to catch cart operations
   */
  function overrideFetch() {
    const originalFetch = window.fetch;
    window.fetch = async function (...args) {
      const result = await originalFetch.apply(this, args);

      const url = args[0];
      if (typeof url === 'string') {
        if (url.includes('/cart/add') || url.includes('/cart/change') || url.includes('/cart/update')) {
          setTimeout(enforceSampleLimit, 300);
          setTimeout(displayFreePrices, 400);
        }
      }

      return result;
    };
  }

  /**
   * Fetch cart data
   */
  async function fetchCart() {
    const response = await fetch('/cart.js');
    return response.json();
  }

  /**
   * Refresh cart drawer dynamically without closing it
   */
  async function refreshCartDrawer() {
    try {
      // Fetch the cart-drawer section
      const response = await fetch('/?section_id=cart-drawer');
      const html = await response.text();

      // Parse and extract cart drawer content
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Update cart drawer items
      const newDrawerItems = doc.querySelector('cart-drawer-items');
      const currentDrawerItems = document.querySelector('cart-drawer-items');
      if (newDrawerItems && currentDrawerItems) {
        currentDrawerItems.innerHTML = newDrawerItems.innerHTML;
      }

      // Update cart footer
      const newFooter = doc.querySelector('.cart-drawer__footer');
      const currentFooter = document.querySelector('.cart-drawer__footer');
      if (newFooter && currentFooter) {
        currentFooter.innerHTML = newFooter.innerHTML;
      }

      // Update cart icon bubble count
      const iconBubbleResponse = await fetch('/?section_id=cart-icon-bubble');
      const iconHtml = await iconBubbleResponse.text();
      const iconDoc = parser.parseFromString(iconHtml, 'text/html');
      const newBubble = iconDoc.querySelector('.cart-count-bubble');
      const currentBubble = document.querySelector('.cart-count-bubble');
      if (newBubble && currentBubble) {
        currentBubble.innerHTML = newBubble.innerHTML;
      }

      console.log('[FreeSamples] Cart drawer refreshed');
    } catch (error) {
      console.error('[FreeSamples] Error refreshing cart drawer:', error);
      // Fallback to reload if refresh fails
      window.location.reload();
    }
  }

  /**
   * Fetch product data
   */
  async function fetchProduct(handle) {
    const response = await fetch(`/products/${handle}.js`);
    return response.json();
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Removed global click handler - MutationObserver handles dynamic content
  // This was causing excessive re-runs and memory pressure

  // Expose for debugging
  window.OjarFreeSamples = {
    enforceSampleLimit,
    displayFreePrices,
    MAX_FREE_SAMPLES
  };

})();
